import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { BookingService, Booking, BookingStatus } from './booking.service';
import { environment } from '../../environments/environment';

/**
 * WebSocket message types from the server.
 */
export interface WebSocketBookingAllocation {
  type: 'booking_allocation';
  booking: {
    booking_id: string;
    client_name: string;
    service_name: string;
    start_dt: string;
    end_dt: string;
    location?: string;
  };
}

export interface WebSocketBookingUpdate {
  type: 'booking_update';
  booking_id: string;
  action: 'cancelled' | 'rescheduled';
  new_status: string;
  client_name: string;
  service_name: string;
  start_dt: string;
  end_dt: string;
}

export type WebSocketMessage = WebSocketBookingAllocation | WebSocketBookingUpdate;

/**
 * WebSocketService manages a persistent WebSocket connection to the backend
 * for receiving real-time booking allocation notifications.
 *
 * Features:
 * - Establishes connection on login using resource JWT
 * - Handles booking_allocation messages: updates BookingService, emits notifications
 * - Reconnects with exponential backoff on disconnect (1s, 2s, 4s, 8s, max 30s)
 * - Exposes `isConnected` and `lastMessage` signals for reactive UI binding
 * - Provides `connect()` and `disconnect()` methods
 *
 * Validates: Requirements 14.1, 14.2, 14.3, 14.7
 */
@Injectable({ providedIn: 'root' })
export class WebSocketService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly bookingService = inject(BookingService);

  /** Whether the WebSocket is currently connected. */
  readonly isConnected = signal(false);

  /** The last message received from the WebSocket. */
  readonly lastMessage = signal<WebSocketMessage | null>(null);

  /** Subject emitting incoming booking allocation messages for component subscription. */
  readonly bookingAllocations$ = new Subject<WebSocketBookingAllocation>();

  /** Subject emitting incoming booking update messages (cancel/reschedule) for component subscription. */
  readonly bookingUpdates$ = new Subject<WebSocketBookingUpdate>();

  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;

  /** Maximum reconnect delay in milliseconds. */
  private static readonly MAX_RECONNECT_DELAY_MS = 30_000;

  /** Base reconnect delay in milliseconds. */
  private static readonly BASE_RECONNECT_DELAY_MS = 1_000;

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Establish a WebSocket connection to the backend.
   * Uses the authenticated resource's ID and JWT token for the connection URL.
   *
   * URL format: wss://api.newlogicqr.com/ws/resource/{resource_id}?token={jwt}
   *
   * Validates: Requirements 14.1
   */
  connect(): void {
    const resourceId = this.auth.resourceId();
    const token = this.auth.getToken();

    if (!resourceId || !token) {
      return;
    }

    // Don't reconnect if already connected
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    this.intentionalClose = false;
    this.createConnection(resourceId, token);
  }

  /**
   * Gracefully close the WebSocket connection.
   * Prevents automatic reconnection.
   */
  disconnect(): void {
    this.intentionalClose = true;
    this.clearReconnectTimer();
    this.closeSocket();
  }

  /**
   * Calculate the reconnect delay based on the current attempt count.
   * Uses exponential backoff: 1s, 2s, 4s, 8s, ..., capped at 30s.
   *
   * Validates: Requirements 14.7
   */
  getReconnectDelay(): number {
    const delay = WebSocketService.BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts);
    return Math.min(delay, WebSocketService.MAX_RECONNECT_DELAY_MS);
  }

  // ---------- Private ----------

  private createConnection(resourceId: string, token: string): void {
    const url = `${environment.wsUrl}?token=${token}&resource_id=${resourceId}`;

    try {
      this.ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.isConnected.set(true);
      this.reconnectAttempts = 0; // Reset backoff on successful connection
    };

    this.ws.onmessage = (event: MessageEvent) => {
      this.handleMessage(event);
    };

    this.ws.onclose = () => {
      this.isConnected.set(false);
      if (!this.intentionalClose) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // The error event is always followed by a close event,
      // so reconnection logic is handled in onclose.
    };
  }

  /**
   * Handle incoming WebSocket messages.
   * Parses JSON and routes by message type.
   *
   * Validates: Requirements 14.2, 14.3
   */
  private handleMessage(event: MessageEvent): void {
    let message: WebSocketMessage;
    try {
      message = JSON.parse(event.data);
    } catch {
      return; // Ignore malformed messages
    }

    this.lastMessage.set(message);

    if (message.type === 'booking_allocation') {
      this.handleBookingAllocation(message);
    } else if (message.type === 'booking_update') {
      this.handleBookingUpdate(message);
    }
  }

  /**
   * Handle a booking allocation message:
   * - Update the bookings list via BookingService
   * - Emit via the bookingAllocations$ subject for notification display
   *
   * Validates: Requirements 14.2, 14.3
   */
  private handleBookingAllocation(message: WebSocketBookingAllocation): void {
    // Create a Booking object from the allocation message
    const booking: Booking = {
      booking_id: message.booking.booking_id,
      tenant_id: this.auth.tenantId() || '',
      resource_id: this.auth.resourceId() || '',
      client_name: message.booking.client_name,
      service_name: message.booking.service_name,
      start_dt: message.booking.start_dt,
      end_dt: message.booking.end_dt,
      status: 'CONFIRMED',
      location: message.booking.location,
    };

    // Update bookings list
    this.bookingService.addBooking(booking);

    // Emit for notification banner display
    this.bookingAllocations$.next(message);
  }

  /**
   * Handle a booking update message (cancelled or rescheduled):
   * - Update the booking status/times in BookingService
   * - Emit via the bookingUpdates$ subject for notification display
   */
  private handleBookingUpdate(message: WebSocketBookingUpdate): void {
    if (message.action === 'cancelled') {
      this.bookingService.updateBookingStatus(message.booking_id, { status: message.new_status as BookingStatus });
    } else if (message.action === 'rescheduled') {
      this.bookingService.updateBookingStatus(message.booking_id, {
        status: message.new_status as BookingStatus,
        start_dt: message.start_dt,
        end_dt: message.end_dt,
      });
    }

    // Emit for notification banner display
    this.bookingUpdates$.next(message);
  }

  /**
   * Schedule a reconnection attempt with exponential backoff.
   * Delay sequence: 1s, 2s, 4s, 8s, 16s, 30s, 30s, ...
   *
   * Validates: Requirements 14.7
   */
  private scheduleReconnect(): void {
    this.clearReconnectTimer();

    const delay = this.getReconnectDelay();
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private closeSocket(): void {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
    this.isConnected.set(false);
  }
}
